<div class="vehicle-management">
  <!-- Header with Actions -->
  <div class="page-header">
    <h1>Vehicle Management</h1>
    <div class="header-actions">
      <button class="btn btn-primary" (click)="startCreate()">
        <i class="material-icons">add</i>
        Add New Vehicle
      </button>
      <button class="btn btn-secondary" (click)="loadVehicles()">
        <i class="material-icons">refresh</i>
        Refresh
      </button>
    </div>
  </div>

  <div class="save-banner error" *ngIf="errorMessage">
    <i class="material-icons">error</i>
    {{ errorMessage }}
  </div>

  <div class="save-banner" *ngIf="successMessage">
    <i class="material-icons">check_circle</i>
    {{ successMessage }}
  </div>

  <!-- Vehicle Form -->
  <div class="vehicle-form-container" *ngIf="showForm">
    <div class="form-header">
      <h2>{{ isEditing ? 'Edit Vehicle' : 'Add New Vehicle' }}</h2>
      <button class="btn-close" (click)="resetForm()">×</button>
    </div>
    
    <form [formGroup]="vehicleForm" (ngSubmit)="onSubmit()" class="vehicle-form">
      <div class="form-row">
        <div class="form-group">
          <label for="vehicleRegistration">Registration Number *</label>
          <input type="text" id="vehicleRegistration" formControlName="vehicleRegistration" 
                 class="form-control" placeholder="CA123456">
          <div class="error" *ngIf="vehicleForm.get('vehicleRegistration')?.invalid && 
                                   vehicleForm.get('vehicleRegistration')?.touched">
            Registration number is required
          </div>
        </div>
        
        <div class="form-group">
          <label for="vehicleIdentificationNumber">Vehicle Identification Number *</label>
          <input type="text" id="vehicleIdentificationNumber" 
                 formControlName="vehicleIdentificationNumber" class="form-control" 
                 placeholder="1HGCM82633A004352">
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="vehicleLabel">Fleet Label</label>
          <input type="text" id="vehicleLabel" formControlName="vehicleLabel"
                 class="form-control" placeholder="Airport shuttle 1">
        </div>

        <div class="form-group">
          <label for="statusID">Status</label>
          <select id="statusID" formControlName="statusID" class="form-control">
            <option *ngFor="let status of statuses" [ngValue]="status.statusID">
              {{ status.statusName }}
            </option>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="organisationID">Organisation</label>
          <select id="organisationID" formControlName="organisationID" class="form-control">
            <option [ngValue]="null">No organisation</option>
            <option *ngFor="let organisation of organisations" [ngValue]="organisation.organisationID">
              {{ getOrganisationOptionLabel(organisation) }}
            </option>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="vehicleManufacturerMake">Make *</label>
          <input type="text" id="vehicleManufacturerMake" 
                 formControlName="vehicleManufacturerMake" class="form-control" 
                 placeholder="Toyota">
        </div>
        
        <div class="form-group">
          <label for="vehicleManufacturerModel">Model *</label>
          <input type="text" id="vehicleManufacturerModel" 
                 formControlName="vehicleManufacturerModel" class="form-control" 
                 placeholder="Quantum">
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="vehicleYear">Year *</label>
          <input type="number" id="vehicleYear" formControlName="vehicleYear" 
                 class="form-control" min="2000">
        </div>
        
        <div class="form-group">
          <label for="vehicleSeatingCapacity">Seating Capacity *</label>
          <input type="number" id="vehicleSeatingCapacity" 
                 formControlName="vehicleSeatingCapacity" class="form-control" min="1">
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="vehicleMileage">Mileage</label>
          <input type="number" id="vehicleMileage" formControlName="vehicleMileage"
                 class="form-control" min="0">
        </div>
      </div>

      <div class="form-group">
        <label for="deviceID">Tracking Device</label>
        <select id="deviceID" formControlName="deviceID" class="form-control">
          <option [ngValue]="null">No Device</option>
          <option *ngFor="let device of availableDevices" [ngValue]="device.deviceID">
            {{ getDeviceOptionLabel(device) }}
          </option>
        </select>
      </div>

      <div class="form-actions">
        <button type="button" class="btn btn-secondary" (click)="resetForm()">Cancel</button>
        <button type="submit" class="btn btn-primary" [disabled]="vehicleForm.invalid || isLoading">
          {{ isLoading ? 'Saving...' : (isEditing ? 'Update Vehicle' : 'Create Vehicle') }}
        </button>
      </div>
    </form>
  </div>

  <!-- Vehicles List -->
  <div class="vehicles-list">
    <div class="list-header">
      <h3>All Vehicles ({{ filteredVehicles.length }})</h3>
      <div class="search-box">
        <input type="text" placeholder="Search vehicles..." class="search-input" [(ngModel)]="searchTerm" (ngModelChange)="applyFilters()">
      </div>
    </div>

    <div class="vehicles-grid">
      <div class="vehicle-card" *ngFor="let vehicle of filteredVehicles">
        <div class="vehicle-header">
          <div class="vehicle-icon">
            <i class="material-icons">directions_car</i>
          </div>
          <div class="vehicle-info">
            <h4>{{ vehicle.vehicleLabel || vehicle.vehicleRegistration }}</h4>
            <p>{{ vehicle.vehicleIdentificationNumber || 'No VIN captured' }}</p>
          </div>
          <div class="vehicle-status" [class.active]="isVehicleActive(vehicle)">
            {{ getVehicleStatusName(vehicle) }}
          </div>
        </div>
        
        <div class="vehicle-details">
          <div class="detail-item">
            <span class="label">Registration:</span>
            <span class="value">{{ vehicle.vehicleRegistration || '-' }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Make:</span>
            <span class="value">{{ vehicle.vehicleManufacturerMake || '-' }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Model:</span>
            <span class="value">{{ vehicle.vehicleManufacturerModel || '-' }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Year:</span>
            <span class="value">{{ vehicle.vehicleYear || '-' }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Capacity:</span>
            <span class="value">{{ vehicle.vehicleSeatingCapacity || 0 }} seats</span>
          </div>
          <div class="detail-item">
            <span class="label">Mileage:</span>
            <span class="value">{{ vehicle.vehicleMileage || 0 }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Organisation:</span>
            <span class="value">{{ getVehicleOrganisationName(vehicle) }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Device serial:</span>
            <span class="value">{{ getVehicleDeviceLabel(vehicle) }}</span>
          </div>
        </div>
        
        <div class="vehicle-actions">
          <button class="btn-action" (click)="editVehicle(vehicle)">
            <i class="material-icons">edit</i>
          </button>
          <button class="btn-action btn-danger" (click)="deleteVehicle(vehicle.vehicleID)">
            <i class="material-icons">delete</i>
          </button>
          <a [routerLink]="['/dashboard/trips']" [queryParams]="{vehicle: vehicle.vehicleID}" 
             class="btn-action">
            <i class="material-icons">schedule</i>
          </a>
        </div>
      </div>
    </div>

    <div class="empty-state" *ngIf="!isLoading && filteredVehicles.length === 0">
      <i class="material-icons">directions_car</i>
      <h3>No vehicles found</h3>
      <p>Add your first vehicle to start tracking</p>
      <button class="btn btn-primary" (click)="startCreate()">
        Add First Vehicle
      </button>
    </div>
  </div>
</div>

<app-confirm-dialog
  *ngIf="vehiclePendingDeleteId"
  title="Delete vehicle"
  message="Delete this vehicle?"
  confirmLabel="Delete"
  tone="danger"
  (confirmed)="confirmDeleteVehicle()"
  (cancelled)="cancelDeleteVehicle()">
</app-confirm-dialog>
